import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Stack,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import { ContactUs } from "../../Services/ApiServices";

const InquireyFormFillSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    propertyValue: "",
    service: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState({ type: "", text: "" });

  const services = ["Acquisition", "Services", "Commercial", "Residential"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceSelect = (service) => {
    setFormData({
      ...formData,
      service: formData.service === service ? "" : service,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setStatusMsg({ type: "error", text: "Please enter your full name." });
      return;
    }
    if (!formData.email.trim()) {
      setStatusMsg({ type: "error", text: "Please enter your email address." });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setStatusMsg({
        type: "error",
        text: "Please enter a valid email address.",
      });
      return;
    }
    if (!formData.message.trim()) {
      setStatusMsg({
        type: "error",
        text: "Please describe your vision or specific property requirements.",
      });
      return;
    }

    setLoading(true);
    setStatusMsg({ type: "", text: "" });

    try {
      const response = await ContactUs({
        name: formData.name,
        email: formData.email,
        propertyValue: formData.propertyValue,
        service: formData.service,
        message: formData.message,
      });

      if (response && response.status === true) {
        setStatusMsg({
          type: "success",
          text: "Your inquiry has been submitted successfully! We'll get back to you shortly.",
        });
        setFormData({
          name: "",
          email: "",
          propertyValue: "",
          service: "",
          message: "",
        });
      } else {
        setStatusMsg({
          type: "error",
          text:
            response?.message || "Failed to submit inquiry. Please try again.",
        });
      }
    } catch (err) {
      console.error(err);
      setStatusMsg({
        type: "error",
        text: "An unexpected error occurred. Please try again later.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      sx={{
        width: "100%",
        maxWidth: "1100px",
        minHeight: "800px",
        bgcolor: "#FFFFFF",
        borderRadius: "24px",
        boxShadow: "0px 20px 40px rgba(0, 0, 0, 0.08)",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        overflow: "hidden",
        mx: "auto",
        position: "relative",
      }}
    >
      {/* Left Pane - Dark (Direct Channels) */}
      <Box
        sx={{
          width: { xs: "100%", md: "40%" },
          bgcolor: "#1A1918",
          color: "#FFFFFF",
          p: { xs: 4, md: 8 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Manrope, sans-serif",
            fontWeight: 400,
            fontSize: "36px",
            color: "#FFFFFF",
            textAlign: "left",
            mb: 8,
          }}
        >
          Direct Channels
        </Typography>

        <Stack spacing={5} sx={{ alignItems: "flex-start" }}>
          {/* General Inquiries Box */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: "#A8A29E", // Light gray for labels in dark mode
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              General Inquiries
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
              }}
            >
              info@abilio.realty
            </Typography>
          </Box>

          {/* Contact Box */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: "#A8A29E",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Contact
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
              }}
            >
              +91 9000 133 018
            </Typography>
          </Box>

          {/* Press & Media Box */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "12px",
                fontWeight: 600,
                color: "#A8A29E",
                textTransform: "uppercase",
                letterSpacing: "1px",
              }}
            >
              Press & Media
            </Typography>
            <Typography
              sx={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
              }}
            >
              media@abilio.realty
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Right Pane - Form */}
      <Box
        sx={{
          width: { xs: "100%", md: "60%" },
          p: { xs: 4, md: 8 },
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          bgcolor: "#FFFFFF",
        }}
      >
        <form
          onSubmit={handleSubmit}
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          {/* Header Section */}
          <Box
            sx={{
              mb: 6,
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              alignItems: "flex-start",
            }}
          >
            <Typography
              sx={{
                fontFamily: "Manrope, sans-serif",
                fontWeight: 400,
                fontSize: "36px",
                color: "#1C1B1B",
                textAlign: "left",
              }}
            >
              Service Enquiry
            </Typography>
          </Box>

          {/* Selectors Section */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              alignItems: "flex-start",
              mb: 6,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: { xs: "wrap", lg: "nowrap" },
                gap: "11.6px",
                alignItems: "flex-start",
                width: "100%",
              }}
            >
              {services.map((service) => (
                <Box
                  key={service}
                  onClick={() => handleServiceSelect(service)}
                  sx={{
                    minWidth: "100px",
                    height: "36px",
                    borderRadius: "10px",
                    bgcolor:
                      formData.service === service ? "#FF5722" : "#E7E5E440",
                    color: formData.service === service ? "#FFFFFF" : "#FF5722",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    transition: "all 0.3s ease",
                    border:
                      formData.service === service
                        ? "1px solid #FF5722"
                        : "1px solid #E7E5E4",
                    fontFamily: "Inter, sans-serif",
                    fontSize: "12px",
                    fontWeight: 500,
                    px: "16px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {service}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Input Fields */}
          <Stack spacing={4} sx={{ width: "100%", alignItems: "flex-start" }}>
            {/* Row 1: Name and Email */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: "24px",
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1, width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    mb: 1,
                    color: "#1C1B1B",
                    textTransform: "uppercase",
                    textAlign: "left",
                    fontFamily: "Inter, sans-serif",
                    letterSpacing: "1px",
                  }}
                >
                  Full Name <span style={{ color: "#FF5722" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  variant="filled"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Johnathan Sterling"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      borderRadius: "6px",
                      bgcolor: "#FFFFFF",
                      border: "1px solid #E7E5E4",
                      transition: "all 0.2s ease",
                      height: "44px",
                      "&.Mui-focused": {
                        bgcolor: "#FFFFFF",
                        borderColor: "#FF5722",
                        boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                      },
                    },
                  }}
                  inputProps={{ style: { textAlign: "left", padding: "0 14px", height: "44px", boxSizing: "border-box" } }}
                />
              </Box>
              <Box sx={{ flex: 1, width: "100%" }}>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    mb: 1,
                    color: "#1C1B1B",
                    textTransform: "uppercase",
                    textAlign: "left",
                    fontFamily: "Inter, sans-serif",
                    letterSpacing: "1px",
                  }}
                >
                  Email Address <span style={{ color: "#FF5722" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  variant="filled"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="sterling@portfolio.com"
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      borderRadius: "6px",
                      bgcolor: "#FFFFFF",
                      border: "1px solid #E7E5E4",
                      transition: "all 0.2s ease",
                      height: "44px",
                      "&.Mui-focused": {
                        bgcolor: "#FFFFFF",
                        borderColor: "#FF5722",
                        boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                      },
                    },
                  }}
                  inputProps={{ style: { textAlign: "left", padding: "0 14px", height: "44px", boxSizing: "border-box" } }}
                />
              </Box>
            </Box>

            {/* Row 2: Property Value Range */}
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  mb: 1,
                  color: "#1C1B1B",
                  textTransform: "uppercase",
                  textAlign: "left",
                  fontFamily: "Inter, sans-serif",
                  letterSpacing: "1px",
                }}
              >
                Property Value Range (Rupee) <span style={{ color: "#FF5722" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                select
                name="propertyValue"
                variant="filled"
                value={formData.propertyValue}
                onChange={handleChange}
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: "6px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid #E7E5E4",
                    transition: "all 0.2s ease",
                    height: "44px",
                    "&.Mui-focused": {
                      bgcolor: "#FFFFFF",
                      borderColor: "#FF5722",
                      boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                    },
                  },
                }}
                SelectProps={{
                  displayEmpty: true,
                  sx: { textAlign: "left", height: "44px", "& .MuiSelect-select": { padding: "0 14px", display: "flex", alignItems: "center", height: "100%" } },
                  MenuProps: { disableScrollLock: true },
                }}
              >
                <MenuItem value="" disabled sx={{ textAlign: "left" }}>
                  Select range
                </MenuItem>
                <MenuItem value="₹70L - ₹5Cr" sx={{ textAlign: "left" }}>
                  ₹70L - ₹5Cr
                </MenuItem>
                <MenuItem value="₹5Cr - ₹10Cr" sx={{ textAlign: "left" }}>
                  ₹5Cr - ₹10Cr
                </MenuItem>
                <MenuItem value="₹10Cr+" sx={{ textAlign: "left" }}>
                  ₹10Cr+
                </MenuItem>
              </TextField>
            </Box>

            {/* Row 3: Initial Project Brief */}
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: "10px",
                  fontWeight: 700,
                  mb: 1,
                  color: "#1C1B1B",
                  textTransform: "uppercase",
                  textAlign: "left",
                  fontFamily: "Inter, sans-serif",
                  letterSpacing: "1px",
                }}
              >
                Initial Project Brief <span style={{ color: "#FF5722" }}>*</span>
              </Typography>
              <TextField
                fullWidth
                name="message"
                variant="filled"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleChange}
                placeholder="Describe your vision or specific property requirements..."
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    borderRadius: "6px",
                    bgcolor: "#FFFFFF",
                    border: "1px solid #E7E5E4",
                    transition: "all 0.2s ease",
                    padding: "12px 16px",
                    "&.Mui-focused": {
                      bgcolor: "#FFFFFF",
                      borderColor: "#FF5722",
                      boxShadow: "0 0 0 2px rgba(255, 87, 34, 0.1)",
                    },
                  },
                }}
                inputProps={{ style: { textAlign: "left", padding: 0 } }}
              />
            </Box>

            {/* Status Message Display */}
            {statusMsg.text && (
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: "8px",
                  bgcolor: statusMsg.type === "success" ? "#ECFDF5" : "#FEF2F2",
                  color: statusMsg.type === "success" ? "#047857" : "#B91C1C",
                  border:
                    statusMsg.type === "success"
                      ? "1px solid #A7F3D0"
                      : "1px solid #FCA5A5",
                  fontSize: "14px",
                  fontFamily: "Inter, sans-serif",
                  textAlign: "left",
                  mt: 2,
                }}
              >
                {statusMsg.text}
              </Box>
            )}

            {/* Footer of card */}
            <Box sx={{ width: "100%", mt: 2 }}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  bgcolor: "#E94E1B", // Adjusted to match the vibrant orange in the second image
                  color: "#FFFFFF",
                  py: 1.5,
                  borderRadius: "8px",
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#D34100" },
                  display: "flex",
                  gap: 1.5,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  "Submit"
                )}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </Box>
  );
};

export default InquireyFormFillSection;
